export interface AuthConfig {
  authority: string;
  knownAuthorities: string[];
  clientId: string;
  /** Space-separated in env: all scopes of the admin API requested for one access token. */
  apiScopes: string[];
  redirectUri: string;
  postLogoutRedirectUri: string;
  /** Origins that may receive the bearer token (the admin API hosts). */
  apiOrigins: string[];
  /** Optional; empty ⇒ full sign-in page (no home-tenant shortcut). */
  domainHint?: string;
}

const REQUIRED = [
  "REACT_APP_ENTRA_AUTHORITY",
  "REACT_APP_ENTRA_KNOWN_AUTHORITIES",
  "REACT_APP_ENTRA_CLIENT_ID",
  "REACT_APP_ENTRA_API_SCOPE",
  "REACT_APP_ENTRA_REDIRECT_URI",
  "REACT_APP_ENTRA_POST_LOGOUT_URI",
  "REACT_APP_ENTRA_API_ORIGINS",
] as const;

type Env = Record<string, string | undefined>;

function list(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

const LOOPBACK_HOSTS = ["localhost", "127.0.0.1", "[::1]"];

/** API origins must be https; plain http only for a loopback DEV gateway; opaque ("null") origins are rejected. */
function toOrigin(value: string): string {
  const invalid = () =>
    new Error(`Entra sign-in is not configured; REACT_APP_ENTRA_API_ORIGINS needs https origins, got: ${value}`);
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw invalid();
  }
  const secure = url.protocol === "https:";
  const loopbackHttp = url.protocol === "http:" && LOOPBACK_HOSTS.includes(url.hostname);
  if (url.origin === "null" || !(secure || loopbackHttp)) throw invalid();
  return url.origin;
}

/** Reads the public Entra settings from env; fails fast and names every missing value. */
export function readAuthConfig(env: Env): AuthConfig {
  const value = (name: string) => (env[name] ?? "").trim();
  const missing = REQUIRED.filter((name) => value(name) === "");
  if (missing.length > 0) {
    throw new Error(`Entra sign-in is not configured; missing env: ${missing.join(", ")}`);
  }
  return {
    authority: value("REACT_APP_ENTRA_AUTHORITY"),
    knownAuthorities: list(value("REACT_APP_ENTRA_KNOWN_AUTHORITIES")),
    clientId: value("REACT_APP_ENTRA_CLIENT_ID"),
    apiScopes: value("REACT_APP_ENTRA_API_SCOPE").split(/\s+/),
    redirectUri: value("REACT_APP_ENTRA_REDIRECT_URI"),
    postLogoutRedirectUri: value("REACT_APP_ENTRA_POST_LOGOUT_URI"),
    apiOrigins: list(value("REACT_APP_ENTRA_API_ORIGINS")).map(toOrigin),
    domainHint: value("REACT_APP_ENTRA_DOMAIN_HINT") || undefined,
  };
}
